import { useEffect, useState } from 'react';
import { getSocket } from '@/hooks/useRealtimeSync';
import { APP_VERSION, remoteVersionSchema, updateVerdict } from '@/lib/appVersion';
import { prefetchServiceWorkerUpdate, reloadToLatestVersion } from '@/lib/serviceWorkerUpdate';

/** Страховка на случай выкладки одного клиента, без перезапуска API. */
export const CHECK_INTERVAL_MS = 10 * 60 * 1000;
/** Отметка принудительной перезагрузки: на какую версию и когда. */
export const FORCED_RELOAD_KEY = 'tracker:forced-reload';
/**
 * Чаще этого на одну и ту же версию не перезагружаемся. Если сервер уже отдаёт новый version.json,
 * а index.html ещё старый, перезагрузка вернёт ту же сборку — и без паузы она крутилась бы в петле.
 */
const FORCED_RELOAD_PAUSE_MS = 30_000;

function recentlyForced(version: string): boolean {
  try {
    const mark = JSON.parse(sessionStorage.getItem(FORCED_RELOAD_KEY) ?? 'null') as { version: string; at: number } | null;
    return mark?.version === version && Date.now() - mark.at < FORCED_RELOAD_PAUSE_MS;
  } catch {
    return false;
  }
}

function markForced(version: string) {
  try {
    sessionStorage.setItem(FORCED_RELOAD_KEY, JSON.stringify({ version, at: Date.now() }));
  } catch {
    // Без sessionStorage петлю не остановить — но и перезагрузку не отменяем.
  }
}

/**
 * Вышла ли новая версия (как в mrbooker, client/src/hooks/useAppUpdate.ts). Критичную (ниже minVersion)
 * не ждёт: перезагружает страницу сразу. Проверяет /version.json, когда переподключился сокет — выкладка
 * перезапускает API, и сокеты рвутся у всех, — когда вкладка снова видна и раз в 10 минут.
 *
 * Возвращает true, пока ждёт человека; раз появившись, не сбрасывается.
 */
export function useAppUpdate(): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let alive = true;
    let prefetched = false;

    const check = async () => {
      let raw: unknown;
      try {
        // Уникальный адрес: cache: 'no-store' обходит только кэш браузера, а не кэши по пути до сервера.
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        raw = await res.json();
      } catch {
        return; // сеть или ответ не JSON — дождёмся следующей проверки
      }
      const parsed = remoteVersionSchema.safeParse(raw);
      if (!alive || !parsed.success) return;

      const remote = parsed.data;
      const verdict = updateVerdict(APP_VERSION, remote);
      if (verdict === 'required' && !recentlyForced(remote.version)) {
        markForced(remote.version);
        void reloadToLatestVersion();
        return;
      }
      if (verdict === 'current') return;
      setAvailable(true);
      if (!prefetched) {
        prefetched = true;
        void prefetchServiceWorkerUpdate();
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };

    const socket = getSocket();
    socket.on('connect', check);
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    void check();

    return () => {
      alive = false;
      socket.off('connect', check);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, []);

  return available;
}
